import axios from "axios";
import { _notify, NotificationType } from "./notification.service";
import { loader } from "react-global-loader";
import config from "../config";

// TODO: Once the env working fine we can remove this line in next Deployment
// const BASE_URL = process.env.BASE_URL || "https://api.theunplan.com/v1/";

const BASE_URL = config.apiUrl;

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

function parseApiError(error: any): { message: string; isAuthError: boolean } {
  const response = error?.response;

  if (!response) {
    if (error?.code === "ERR_NETWORK") {
      return {
        message: "Unable to reach the server. Check your connection and try again.",
        isAuthError: false,
      };
    }
    return { message: DEFAULT_ERROR_MESSAGE, isAuthError: false };
  }

  const errorResponse = response.data;
  let messages: string[] = [];
  let isAuthError = false;

  if (typeof errorResponse === "string") {
    const trimmed = errorResponse.trim();
    if (trimmed) {
      messages.push(trimmed);
    }
  } else if (errorResponse && typeof errorResponse === "object") {
    if (typeof errorResponse.detail === "string" && errorResponse.detail.trim()) {
      messages.push(errorResponse.detail.trim());
    }

    for (const key in errorResponse) {
      if (key === "code" || key === "detail") {
        continue;
      }

      const value = errorResponse[key];
      if (Array.isArray(value)) {
        messages.push(
          ...value
            .filter((item) => typeof item === "string" && item.trim())
            .map((item) => item.trim())
        );
      } else if (typeof value === "string" && value.trim()) {
        if (value === "Authentication credentials were not provided.") {
          isAuthError = true;
        }
        messages.push(value.trim());
      }
    }
  }

  let message = Array.from(new Set(messages)).join(", ").trim();
  if (!message && !isAuthError) {
    const status = response.status;
    message = status
      ? `Request failed (${status}). Please try again.`
      : DEFAULT_ERROR_MESSAGE;
  }

  if (message.length > 200) {
    message = `${message.slice(0, 200)}...`;
  }

  return { message, isAuthError };
}

enum Request {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  FILE = "FILE",
  PATCH = "PATCH",
}

const _get = (url: string, config = {}, suppressLoader?: boolean) => {
  return _request(Request.GET, url, {}, config, false, suppressLoader);
};

const _delete = (url: string, config = {}, suppressLoader?: boolean) => {
  return _request(Request.DELETE, url, {}, config, false, suppressLoader);
};

const _put = (url: string, data = {}, config = {}) => {
  return _request(Request.PUT, url, data, config);
};

const _post = (
  url: string,
  data = {},
  config = {},
  shouldIgnoreError?: boolean,
  suppressLoader?: boolean
) => {
  return _request(Request.POST, url, data, config, !!shouldIgnoreError, suppressLoader);
};

const _patch = (
  url: string,
  data = {},
  config = {},
  shouldIgnoreError?: boolean
) => {
  return _request(Request.PATCH, url, data, config, !!shouldIgnoreError);
};

const _file = (url: string, data: any = {}, config = {}, suppressLoader?: boolean) => {
  const formData = new FormData();
  formData.append("file_type", data.fileType);
  formData.append("media_file", data.file);
  return _request(Request.FILE, url, formData, config, false, suppressLoader);
};

const _request: any = async (
  type: Request,
  url: string,
  data: any = {},
  config: any = {},
  shouldIgnoreError?: boolean,
  suppressLoader?: boolean
) => {
  let loaderShown = false;
  if (!suppressLoader) {
    loader.show();
    loaderShown = true;
  }
  const apiClient = axios.create({
    baseURL: BASE_URL,
  });
  const contentType =
    type === Request.FILE ? "multipart/form-data" : "application/json";
  apiClient.defaults.headers.post["Content-Type"] = contentType;
  apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("TOKEN");
      if (token) {
        // config.headers["ngrok-skip-browser-warning"] = true;
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const sendRequest = () => {
    switch (type) {
      case Request.GET:
        // apiClient.defaults.headers.get["ngrok-skip-browser-warning"] = "hello";
        return apiClient.get(url, config);
      case Request.POST:
      case Request.FILE:
        // apiClient.defaults.headers.post["ngrok-skip-browser-warning"] = "hello";
        return apiClient.post(url, data, config);
      case Request.PATCH:
        // apiClient.defaults.headers.patch["ngrok-skip-browser-warning"] = "hello";
        return apiClient.patch(url, data, config);
      case Request.PUT:
        return apiClient.put(url, data, config);
      case Request.DELETE:
        // apiClient.defaults.headers.post["ngrok-skip-browser-warning"] = "hello";
        return apiClient.delete(url, config);
    }
  };

  try {
    const response = await sendRequest();
    if (loaderShown) loader.hide();
    return response?.data ?? null;
  } catch (error: any) {
    if (loaderShown) loader.hide();
    if (axios.isCancel(error)) return null;
    const response = error?.response;
    if (response?.status === 401 && response?.data?.code === "token_not_valid") {
      const refresh = localStorage.getItem("REFRESH_TOKEN");
      try {
        const tokenResponse = await axios.post(
          `${BASE_URL}users/api/token/refresh/`,
          { refresh }
        );
        const access = tokenResponse.data.access;
        localStorage.setItem("TOKEN", access);
        config._retry = true;
        return _request(type, url, data, config, shouldIgnoreError);
      } catch (refreshError: any) {
        if (refreshError?.response?.status === 401) {
          localStorage.removeItem("TOKEN");
          localStorage.removeItem("REFRESH_TOKEN");
          localStorage.removeItem("IMAGE_TO_BE_DELTED");
          window.location.href = `${window.location.origin}/login`;
          return;
        }
      }
    }

    const errorResponse = error?.response?.data || null;
    if (shouldIgnoreError) {
      return null;
    }
    if (errorResponse?.is_email_verified === "False") {
      window.location.href = "/activate-account";
      return;
    }
    if (errorResponse?.code === "token_not_valid") {
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("IMAGE_TO_BE_DELTED");
      window.location.href = "/login";
      return;
    }

    if (errorResponse?.code === "no_active_subscription") {
      window.location.href = "/plans";
      return;
    }

    const { message: errorMsg, isAuthError } = parseApiError(error);

    if (!isAuthError) {
      _notify(NotificationType.ERROR, errorMsg);
    } else {
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("IMAGE_TO_BE_DELTED");
      window.location.href = `${window.location.origin}/login`;
    }
    return null;
  }
};

export { _get, _delete, _put, _post, _file, _patch };
